
import { SearchResult } from "@/types/inventory";
import { fetchInventoryData } from "./api/inventoryApi";
import { streamInventoryData } from "./streaming/sseClient";

export { fetchInventoryData };

export { streamInventoryData };

export const streamAllSearches = async (
  terms: string[],
  onSearchUpdate: (index: number, result: SearchResult) => void,
  onAllComplete: (results: SearchResult[]) => void
): Promise<void> => {
  console.log(`🔍 [INVENTORY] Starting streamAllSearches for terms: ${terms.join(', ')}`);
  
  const results: SearchResult[] = Array(terms.length).fill(null);
  let completedCount = 0;
  
  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];
    
    console.log(`🔍 [INVENTORY] Processing term "${term}" (index ${i})`);
    
    // Always stream fresh data from backend
    console.log(`🔍 [INVENTORY] Starting stream for "${term}"`);
    streamInventoryData(
      term,
      // On each update
      (result) => {
        console.log(`📥 [INVENTORY] Stream update for "${term}": ${result.items.length} items`);
        result.items.forEach((item, idx) => {
          console.log(`📥 [INVENTORY]   Stream item ${idx + 1}: "${item.name}" - Store: ${item.storeQty}, Warehouse: ${item.warehouseQty}`);
        });
        results[i] = result;
        onSearchUpdate(i, result);
      },
      // On complete
      (finalResult) => {
        console.log(`✅ [INVENTORY] Stream complete for "${term}": ${finalResult.items.length} final items`);
        finalResult.items.forEach((item, idx) => {
          console.log(`✅ [INVENTORY]   Final item ${idx + 1}: "${item.name}" - Store: ${item.storeQty}, Warehouse: ${item.warehouseQty}`);
        });
        results[i] = finalResult;
        completedCount++;
        
        if (completedCount === terms.length) {
          // All searches are complete
          console.log(`🏁 [INVENTORY] All ${terms.length} searches complete`);
          const validResults = results.filter(Boolean);
          console.log(`🏁 [INVENTORY] ${validResults.length} valid results ready`);
          onAllComplete(validResults);
        }
      }
    ).catch(error => {
      console.error(`🔥 [INVENTORY] Error streaming search for '${term}':`, error);
      
      // Handle guest limit errors by creating an empty result with counter data
      if (error.isGuestLimitError && error.backendData) {
        console.log(`🚫 [INVENTORY] Guest limit error detected, creating result with counter data`);
        const guestLimitResult: SearchResult = {
          searchTerm: term,
          items: [],
          timestamp: new Date().toISOString(),
          searchCount: error.backendData.searchCount,
          remainingSearches: error.backendData.remainingSearches
        };
        results[i] = guestLimitResult;
        onSearchUpdate(i, guestLimitResult);
        
        // Mark as complete and don't re-throw for guest limit errors
        completedCount++;
        
        if (completedCount === terms.length) {
          console.log(`🏁 [INVENTORY] All searches complete (with guest limit error)`);
          const validResults = results.filter(Boolean);
          onAllComplete(validResults);
        }
        
        // Don't re-throw guest limit errors since we've handled them
        return;
      }
      
      // For other errors, still increment counter
      completedCount++;
      
      if (completedCount === terms.length) {
        // All searches are complete (even with errors)
        console.log(`🏁 [INVENTORY] All searches complete (with errors)`);
        const validResults = results.filter(Boolean);
        onAllComplete(validResults);
      }
      
      // Only re-throw non-guest-limit errors
      throw error;
    });
  }
};

export const performAllSearches = async (terms: string[]): Promise<SearchResult[]> => {
  const results: SearchResult[] = [];

  for (const term of terms) {
    const result = await fetchInventoryData(term);
    results.push(result);
  }

  return results;
};

export const scheduleInventoryCheck = () => {
  const now = new Date();
  const targetHour = 8;

  let targetTime = new Date(now);
  targetTime.setHours(targetHour, 0, 0, 0);

  if (now.getHours() >= targetHour) {
    targetTime.setDate(targetTime.getDate() + 1);
  }

  const delay = targetTime.getTime() - now.getTime();
  console.log(`Scheduling next check at ${targetTime.toLocaleString()}`);

  return setTimeout(() => {
    // Note: Without localStorage, we'd need another way to get stored terms
    // This functionality may need to be updated based on your backend
    console.log("Scheduled inventory check - localStorage removed");
  }, delay);
};
